import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

const RING = "#002F6C";
const STEM = "#00B5E2";

/**
 * Loading spinner — GITO power icon with geometrically centered rotate.
 */
const LoadingSpinner = ({
  text,
  className = "",
  delayMs = 300,
  compact = false,
}) => {
    const { t } = useTranslation();
    const [isVisible, setIsVisible] = useState(delayMs === 0);

    useEffect(() => {
        if (delayMs === 0) {
            setIsVisible(true);
            return undefined;
        }

        setIsVisible(false);

        const timeout = setTimeout(() => {
            setIsVisible(true);
        }, delayMs);

        return () => {
            clearTimeout(timeout);
        };
    }, [delayMs]);

    if (!isVisible) {
        return null;
    }

    const outerLayout = compact ? "gap-1 py-3" : "gap-2 py-8";
    const sizeClass = compact ? "h-10 w-10" : "h-14 w-14";

    return (
        <div
            className={`flex flex-col items-center justify-center ${outerLayout} ${className}`.trim()}
        >
            <div
                className={`${sizeClass} loading-power-spin shrink-0`}
                aria-hidden="true"
            >
                <svg
                    viewBox="0 0 48 48"
                    className="block h-full w-full"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    {/* Circle center = 24,24 — open gap at top */}
                    <path
                        d="M32.03 12.54 A14 14 0 1 1 15.97 12.54"
                        stroke={RING}
                        strokeWidth="5.5"
                        strokeLinecap="round"
                    />
                    {/* Stem on vertical axis through center */}
                    <line
                        x1="24"
                        y1="7"
                        x2="24"
                        y2="20"
                        stroke={STEM}
                        strokeWidth="5.5"
                        strokeLinecap="round"
                    />
                </svg>
            </div>

            <p className="text-sm tracking-wide text-mute-500">
                {text ?? t("common.loading")}
            </p>

            <style>{`
        @keyframes loadingPowerSpin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }

        .loading-power-spin {
          transform-origin: center center;
          animation: loadingPowerSpin 1.2s linear infinite;
          will-change: transform;
        }
      `}</style>
        </div>
    );
};

LoadingSpinner.propTypes = {
    text: PropTypes.string,
    className: PropTypes.string,
    delayMs: PropTypes.number,
    compact: PropTypes.bool,
};

export default LoadingSpinner;
